import React from "react";
import { Button, Column, Conditional, Else, If, KeyWithValueType, makeClassName, Row } from "@hex-insights/core";
import { FormValues, Label, RadioFieldValue, useFieldState } from "@hex-insights/forms";
import { Icon } from "../../../../Components/Icon";
import { BaseFieldProps } from "../../shared";
import styles from "./styles.module.css";

enum NameOrder {
	FamilyGiven = "family_given",
	GivenFamily = "given_family",
}

export type NameOrderFieldProps<FV extends FormValues, T extends RadioFieldValue> = BaseFieldProps<FV> & {
	name: KeyWithValueType<FV, T>;
	givenName: string;
	familyName: string;
	nickname?: string;
};

export function NameOrderField<FV extends FormValues, T extends RadioFieldValue>({
	formState,
	name,
	label = "Full Name",
	noHeader = false,
	givenName,
	familyName,
	nickname = "",
	className,
	style,
}: NameOrderFieldProps<FV, T>) {
	const { nameOrder } = formState.formValues;

	const fieldState = useFieldState({ formState, name });
	const { setValue } = fieldState;

	const onClick = React.useCallback(() => {
		setValue((prev) => (prev === NameOrder.FamilyGiven ? NameOrder.GivenFamily : NameOrder.FamilyGiven) as T);
	}, [setValue]);

	return (
		<Column
			justify="spaced-start"
			verticalSpacing="0.25rem"
			className={makeClassName(styles["name-order"], className)}
			style={style}
		>
			<If condition={!noHeader}>
				<Label htmlFor={fieldState.id}>{label}</Label>
			</If>
			<span style={{ fontSize: "var(--text-field__value---font-size)" }}>
				<Conditional>
					<If condition={nameOrder === NameOrder.FamilyGiven}>
						<strong style={{ color: familyName === "" ? "var(--neutral-colors--tertiary-point-two-five)" : undefined }}>
							{familyName || "Family"}
						</strong>{" "}
						<strong style={{ color: givenName === "" ? "var(--neutral-colors--tertiary-point-two-five)" : undefined }}>
							{givenName || "Given"}
						</strong>
						<If condition={nickname !== ""}>
							{" "}
							<strong>({nickname})</strong>
						</If>
					</If>
					<Else>
						<strong style={{ color: givenName === "" ? "var(--neutral-colors--tertiary-point-two-five)" : undefined }}>
							{givenName || "Given"}
						</strong>{" "}
						<If condition={nickname !== ""}>
							<strong>({nickname})</strong>{" "}
						</If>
						<strong style={{ color: familyName === "" ? "var(--neutral-colors--tertiary-point-two-five)" : undefined }}>
							{familyName || "Family"}
						</strong>
					</Else>
				</Conditional>
			</span>
			<Row justify="flex-end">
				<Button variant="link" size="small" onClick={onClick}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						<Icon.ArrowsLeftRight size={14} /> <span>Reverse Name Order</span>
					</Row>
				</Button>
			</Row>
		</Column>
	);
}
